import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from './lib/axiosClient';

function useIsMobile() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => { const fn = () => setW(window.innerWidth); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, []);
  return { isMobile: w < 768, isTablet: w < 1024 };
}

const C = {
  bg: '#1a1c2e', panel: '#12121f', border: '#1e1e35', text: '#f0f0f2',
  muted: '#666', accent: '#ff6b2b', accent2: '#ffab40', green: '#00b894',
  red: '#d63031', purple: '#6c5ce7', blue: '#0984e3', amber: '#f59e0b',
};

function MiniChart({ data, dataKey, color, height = 60, width = 200 }) {
  if (!data || data.length === 0) return null;
  const vals = data.map(d => d[dataKey] || 0);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;
  const points = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4);
    return x + ',' + y;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={'g-' + dataKey} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={width + ',' + height + ' 0,' + height + ' ' + points} fill={'url(#g-' + dataKey + ')'} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function Badge({ color, children }) {
  return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: color + '20', color: color }}>{children}</span>;
}

function Card({ children, style, onClick }) {
  return <div onClick={onClick} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, ...style, cursor: onClick ? 'pointer' : 'default' }}>{children}</div>;
}

function StatBox({ icon, value, label, color, trend, sparkData, sparkKey }) {
  return (
    <Card style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, color: color, letterSpacing: -1 }}>{value}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
          {trend && <div style={{ fontSize: 11, color: trend > 0 ? C.green : C.red, marginTop: 4 }}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={{ fontSize: 20, opacity: 0.6 }}>{icon}</span>
          {sparkData && <MiniChart data={sparkData} dataKey={sparkKey} color={color} height={30} width={80} />}
        </div>
      </div>
    </Card>
  );
}

// ── MRR CHART ──────────────────────────────
function MRRChart({ history, loading }) {
  if (loading || !history || history.length === 0) return <Card style={{ padding: 20 }}><p style={{ color: C.muted }}>Loading MRR...</p></Card>;

  const maxMRR = Math.max(...history.map(h => h.mrr), 1);
  const maxRev = Math.max(...history.map(h => h.bookingRevenue || 0), 1);
  const latest = history[history.length - 1] || {};
  const prev = history[history.length - 8] || {};
  const mrrTrend = prev.mrr > 0 ? Math.round(((latest.mrr - prev.mrr) / prev.mrr) * 100) : 0;

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: 0 }}>📈 Revenue & MRR</h3>
        <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
          <span style={{ color: C.accent }}><span style={{ display: 'inline-block', width: 12, height: 3, background: C.accent, borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />MRR</span>
          <span style={{ color: C.green }}><span style={{ display: 'inline-block', width: 12, height: 3, background: C.green, borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Bookings</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
        <div><div style={{ fontSize: 24, fontWeight: 800, color: C.accent }}>${latest.mrr || 0}</div><div style={{ fontSize: 10, color: C.muted }}>CURRENT MRR {mrrTrend !== 0 && <span style={{ color: mrrTrend > 0 ? C.green : C.red }}>{mrrTrend > 0 ? '↑' : '↓'}{Math.abs(mrrTrend)}%</span>}</div></div>
        <div><div style={{ fontSize: 24, fontWeight: 800, color: C.green }}>{latest.paid || 0}</div><div style={{ fontSize: 10, color: C.muted }}>PAYING</div></div>
        <div><div style={{ fontSize: 24, fontWeight: 800, color: C.amber }}>{latest.trial || 0}</div><div style={{ fontSize: 10, color: C.muted }}>TRIALS</div></div>
      </div>
      <svg width="100%" height="120" viewBox="0 0 600 120" preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.accent} stopOpacity="0.2" /><stop offset="100%" stopColor={C.accent} stopOpacity="0" /></linearGradient>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.green} stopOpacity="0.15" /><stop offset="100%" stopColor={C.green} stopOpacity="0" /></linearGradient>
        </defs>
        {/* MRR line */}
        <polygon points={history.map((h, i) => (i / (history.length - 1)) * 600 + ',' + (120 - (h.mrr / maxMRR) * 100)).join(' ') + ' 600,120 0,120'} fill="url(#mrrGrad)" />
        <polyline points={history.map((h, i) => (i / (history.length - 1)) * 600 + ',' + (120 - (h.mrr / maxMRR) * 100)).join(' ')} fill="none" stroke={C.accent} strokeWidth="2.5" />
        {/* Booking revenue bars */}
        {history.map((h, i) => {
          const x = (i / history.length) * 600;
          const w = 600 / history.length - 2;
          const barH = maxRev > 0 ? (h.bookingRevenue / maxRev) * 80 : 0;
          return <rect key={i} x={x} y={120 - barH} width={Math.max(w, 2)} height={barH} fill={C.green} opacity="0.35" rx="1" />;
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.muted, marginTop: 4 }}>
        <span>{history[0]?.date?.slice(5)}</span><span>{history[Math.floor(history.length / 2)]?.date?.slice(5)}</span><span>{latest.date?.slice(5)}</span>
      </div>
    </Card>
  );
}

// ── TODAY FOCUS ────────────────────────────────────────────────
function TodayFocus({ tenants, mrr }) {
  const expiring = (tenants || []).filter(t => {
    if (!t.trial_ends_at) return false;
    const days = Math.ceil((new Date(t.trial_ends_at) - new Date()) / 86400000);
    return days >= 0 && days <= 3;
  });
  let msg = '', color = '#f5a623', icon = '📞';
  if (expiring.length > 0) {
    const days = Math.ceil((new Date(expiring[0].trial_ends_at) - new Date()) / 86400000);
    msg = 'Call ' + expiring[0].business_name + ' — trial expires in ' + days + ' day' + (days === 1 ? '' : 's') + '. Close opportunity.';
    color = '#ef4444'; icon = '🔥';
  } else if (mrr === 0) {
    msg = 'No paying subscribers yet. Your only job today: book 3 demos. Everything else can wait.';
  } else {
    msg = 'Keep going — follow up on open conversations and push demos toward close.';
    color = '#22c55e'; icon = '✅';
  }
  return (
    <div style={{ background: 'linear-gradient(135deg,#1a1200,#1a0a00)', border: '1px solid ' + color + '40', borderLeft: '4px solid ' + color, borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
      <span style={{ fontSize: 28 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: color, letterSpacing: 1, marginBottom: 4 }}>TODAY'S FOCUS</div>
        <div style={{ fontSize: 14, color: '#f0f0f2', fontWeight: 500, lineHeight: 1.5 }}>{msg}</div>
      </div>
    </div>
  );
}
function MilestoneTracker({ mrr }) {
  const milestones = [
    { clients: 1, mrr: 249, label: 'First Client' },
    { clients: 5, mrr: 1245, label: 'Proof of Concept' },
    { clients: 10, mrr: 2490, label: 'Side Income' },
    { clients: 15, mrr: 3735, label: 'Quit Zone' },
    { clients: 21, mrr: 5200, label: 'Safe Quit' },
    { clients: 30, mrr: 7470, label: 'Hire Help' },
  ];
  const current = milestones.filter(m => mrr >= m.mrr).length;
  return (
    <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, padding: '18px 22px', marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 16 }}>🗺️ Your Roadmap</div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {milestones.map((m, i) => {
          const done = mrr >= m.mrr;
          const active = i === current;
          return (
            <React.Fragment key={m.clients}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: done ? '#f5a623' : '#1a1c2e', border: '2px solid ' + (done ? '#f5a623' : active ? '#f5a623' : '#1e293b'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: done ? '#000' : active ? '#f5a623' : '#374151', boxShadow: active ? '0 0 12px #f5a62360' : 'none' }}>
                  {done ? '✓' : m.clients}
                </div>
                <div style={{ fontSize: 10, color: done ? '#f5a623' : '#64748b', fontWeight: 600, marginTop: 6, textAlign: 'center', lineHeight: 1.3 }}>{m.label}</div>
                <div style={{ fontSize: 10, color: '#374151', marginTop: 2 }}>${m.mrr.toLocaleString()}</div>
              </div>
              {i < milestones.length - 1 && <div style={{ flex: 1, height: 2, background: mrr >= milestones[i+1].mrr ? '#f5a623' : '#1e293b', marginBottom: 28, minWidth: 8 }} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
function OutreachCounter() {
  const todayKey = 'outreach_' + new Date().toISOString().split('T')[0];
  const [count, setCount] = React.useState(() => parseInt(localStorage.getItem(todayKey) || '0'));
  const goal = 5;
  const increment = () => { const n = count + 1; setCount(n); localStorage.setItem(todayKey, String(n)); };
  const decrement = () => { const n = Math.max(0, count - 1); setCount(n); localStorage.setItem(todayKey, String(n)); };
  const pct = Math.min(100, Math.round((count / goal) * 100));
  const color = count >= goal ? '#22c55e' : count >= 3 ? '#f5a623' : '#ef4444';
  return (
    <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, padding: '18px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>📲 Daily Outreach</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Goal: {goal} contacts/day</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={decrement} style={{ width: 32, height: 32, borderRadius: '50%', background: '#1e293b', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' }}>−</button>
          <div style={{ fontSize: 36, fontWeight: 800, color, minWidth: 48, textAlign: 'center' }}>{count}</div>
          <button onClick={increment} style={{ width: 32, height: 32, borderRadius: '50%', background: '#f5a623', border: 'none', color: '#000', fontSize: 20, cursor: 'pointer', fontWeight: 800 }}>+</button>
        </div>
      </div>
      <div style={{ height: 8, background: '#1e293b', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: 4, transition: 'width 0.3s ease' }} />
      </div>
      {count >= goal && <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 600, marginTop: 8 }}>🎉 Daily goal hit!</div>}
    </div>
  );
}
function ProspectTracker() {
  const KEY = 'ivision_prospects_v1';
  const [prospects, setProspects] = React.useState(() => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } });
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', phone: '', status: 'contacted', note: '' });
  const save = (u) => { setProspects(u); localStorage.setItem(KEY, JSON.stringify(u)); };
  const add = () => { if (!form.name.trim()) return; save([{ id: Date.now(), ...form }, ...prospects]); setForm({ name: '', phone: '', status: 'contacted', note: '' }); setShowAdd(false); };
  const STATUS_COLOR = { contacted: '#3b82f6', demo_booked: '#f5a623', closed: '#22c55e', ghosted: '#64748b', not_interested: '#ef4444' };
  const STATUS_LABEL = { contacted: 'Contacted', demo_booked: 'Demo Booked', closed: 'Closed', ghosted: 'Ghosted', not_interested: 'Not Interested' };
  const inp = { background: '#1a1c2e', border: '1px solid #1e293b', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  return (
    <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, padding: '18px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>📋 Prospect Pipeline</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{prospects.filter(p => p.status !== 'closed' && p.status !== 'not_interested').length} active · {prospects.filter(p => p.status === 'closed').length} closed</div>
        </div>
        <button onClick={() => setShowAdd(p => !p)} style={{ background: '#f5a623', color: '#000', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{showAdd ? 'Cancel' : '+ Add'}</button>
      </div>
      {showAdd && (
        <div style={{ background: '#1a1c2e', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div><div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>NAME</div><input style={inp} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Acme Rentals" /></div>
            <div><div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>PHONE</div><input style={inp} value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 702 555 0000" /></div>
            <div><div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>STATUS</div><select style={inp} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>{Object.entries(STATUS_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </div>
          <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>NOTE</div><input style={inp} value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} placeholder="Called Tuesday, following up Friday..." /></div>
          <button onClick={add} style={{ background: '#f5a623', color: '#000', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Save</button>
        </div>
      )}
      {prospects.length === 0
        ? <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No prospects yet. Add your first target.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto' }}>
            {prospects.map(p => (
              <div key={p.id} style={{ background: '#1a1c2e', borderRadius: 10, padding: '12px 16px', borderLeft: '3px solid ' + (STATUS_COLOR[p.status] || '#64748b') }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{p.name}</div>
                    {p.phone && <div style={{ fontSize: 12, color: '#64748b' }}>{p.phone}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select style={{ background: '#1e293b', border: 'none', borderRadius: 4, padding: '3px 6px', color: STATUS_COLOR[p.status] || '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }} value={p.status} onChange={e => save(prospects.map(x => x.id === p.id ? { ...x, status: e.target.value } : x))}>
                      {Object.entries(STATUS_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button onClick={() => save(prospects.filter(x => x.id !== p.id))} style={{ background: 'none', border: 'none', color: '#374151', cursor: 'pointer', fontSize: 16 }}>×</button>
                  </div>
                </div>
                <input style={{ background: 'transparent', border: 'none', borderBottom: '1px solid #1e293b', color: '#94a3b8', fontSize: 12, width: '100%', padding: '4px 0', outline: 'none' }} value={p.note} onChange={e => save(prospects.map(x => x.id === p.id ? { ...x, note: e.target.value } : x))} placeholder="Add a note..." />
              </div>
            ))}
          </div>
      }
    </div>
  );
}
function TenantHealthTable({ tenants, onLoginAs, onResetPw }) {
  const PLAN_PRICE = { founding: 99, standard: 199, growth: 149, professional: 249, pro: 249, enterprise: 399, perpetual: 0, trial: 0 };
  const getAction = (t) => {
    if (t.plan === 'perpetual') return { msg: 'Perpetual — deliver & collect $4k', color: '#a855f7' };
    if (t.plan !== 'trial') return { msg: 'Paying — retain', color: '#22c55e' };
    if (!t.trial_ends_at) return { msg: 'Demo account', color: '#64748b' };
    const days = Math.ceil((new Date(t.trial_ends_at) - new Date()) / 86400000);
    if (days < 0) return { msg: 'Expired — close or remove', color: '#ef4444' };
    if (days <= 3) return { msg: 'Expires in ' + days + 'd — call now!', color: '#ef4444' };
    if (days <= 7) return { msg: 'Expires in ' + days + 'd — follow up', color: '#f5a623' };
    return { msg: 'Monitoring', color: '#64748b' };
  };
  return (
    <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>🏢 Tenant Health</div>
        <div style={{ fontSize: 11, color: '#64748b' }}>{tenants.length} businesses</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 90px 100px 1fr 90px', padding: '8px 20px', background: '#1a1c2e', fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 1 }}>
        <div>BUSINESS</div><div>PLAN</div><div>STATUS</div><div>NEXT ACTION</div><div>ACTIONS</div>
      </div>
      {tenants.map((t, i) => {
        const action = getAction(t);
        return (
          <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '1.2fr 90px 100px 1fr 90px', padding: '12px 20px', borderTop: '1px solid #1e293b', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{t.business_name}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{t.business_type}</div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.plan !== 'trial' ? '#f5a623' : '#64748b' }}>
              {t.plan === 'perpetual' ? 'Perpetual' : t.plan === 'trial' ? 'Trial' : t.plan}
              {t.plan !== 'trial' && t.plan !== 'perpetual' && <div style={{ fontSize: 10, color: '#22c55e' }}>${PLAN_PRICE[t.plan] || 249}/mo</div>}
            </div>
            <div><span style={{ fontSize: 11, fontWeight: 700, color: t.status === 'active' ? '#22c55e' : '#ef4444', background: (t.status === 'active' ? '#22c55e' : '#ef4444') + '15', padding: '3px 8px', borderRadius: 4 }}>{t.status}</span></div>
            <div style={{ fontSize: 12, color: action.color, fontWeight: 500 }}>{action.msg}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => onLoginAs(t)} style={{ background: '#1e293b', border: 'none', borderRadius: 5, padding: '5px 8px', color: '#94a3b8', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>View</button>
              <button onClick={() => onResetPw(t)} style={{ background: '#1e293b', border: 'none', borderRadius: 5, padding: '5px 8px', color: '#94a3b8', fontSize: 11, cursor: 'pointer' }}>🔑</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── PIPELINE ──────────────────────────────
function Pipeline({ pipeline, loading, onLoginAs }) {
  const stages = [
    { key: 'lead', label: 'Leads', icon: '🎯', color: C.muted },
    { key: 'trial', label: 'Trial', icon: '⏳', color: C.amber },
    { key: 'paid', label: 'Paid', icon: '✅', color: C.green },
    { key: 'churned', label: 'Churned', icon: '💀', color: C.red },
  ];

  return (
    <Card style={{ padding: 20 }}>
      <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: '0 0 16px' }}>🔄 Onboarding Pipeline</h3>
      {loading ? <p style={{ color: C.muted }}>Loading...</p> : (
        <div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {stages.map(s => {
              const count = (pipeline?.[s.key] || []).length;
              const total = Object.values(pipeline || {}).reduce((sum, arr) => sum + arr.length, 0) || 1;
              return (
                <div key={s.key} style={{ flex: Math.max(count / total, 0.08), textAlign: 'center' }}>
                  <div style={{ height: 8, background: s.color, borderRadius: 4, opacity: 0.8, marginBottom: 6 }} />
                  <div style={{ fontSize: 11, color: s.color, fontWeight: 700 }}>{count}</div>
                  <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                </div>
              );
            })}
          </div>
          {stages.map(s => {
            const items = pipeline?.[s.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={s.key} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: s.color, marginBottom: 8 }}>{s.icon} {s.label} ({items.length})</div>
                {items.slice(0, 4).map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid ' + C.border + '40' }}>
                    <div>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{item.type} · {item.email}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {item.daysLeft != null && <Badge color={item.daysLeft <= 3 ? C.red : C.amber}>{item.daysLeft <= 0 ? 'Expired' : item.daysLeft + 'd'}</Badge>}
                      <button onClick={() => onLoginAs(item)} style={{ padding: '3px 8px', background: C.purple + '20', border: 'none', borderRadius: 6, color: C.purple, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>View →</button>
                    </div>
                  </div>
                ))}
                {items.length > 4 && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>+{items.length - 4} more</div>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── DEMO MANAGEMENT ──────────────────────────
function DemoManager({ tenants, onSeed, onReset, seedLoading, onResetPw }) {
  const [copied, setCopied] = React.useState(null);
  const VERT = { barbershop: { icon: '💈', color: '#f97316', label: 'Barbershop' }, spa: { icon: '🧖', color: '#a78bfa', label: 'Spa' }, auto_shop: { icon: '🔧', color: '#f59e0b', label: 'Auto Shop' }, auto: { icon: '🔧', color: '#f59e0b', label: 'Auto' }, smoke_shop: { icon: '🌿', color: '#22c55e', label: 'Smoke Shop' }, motorcycle: { icon: '🏍️', color: '#ef4444', label: 'Motorcycle' }, salon: { icon: '✂️', color: '#ec4899', label: 'Salon' } };
  const copyUrl = (url, id) => { navigator.clipboard.writeText(window.location.origin + url); setCopied(id); setTimeout(() => setCopied(null), 2000); };
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: 0 }}>🧪 Demo Environments</h3>
        <span style={{ color: C.muted, fontSize: 12 }}>{(tenants || []).length} demos available</span>
      </div>
      {(tenants || []).map(t => {
        const v = VERT[t.business_type] || { icon: '🏢', color: '#64748b', label: t.business_type || 'Other' };
        const siteUrl = '/site/' + (t.subdomain || '');
        const bookUrl = '/book/' + (t.subdomain || '');
        return (
          <Card key={t.id} style={{ padding: 0, overflow: 'hidden', borderLeft: '3px solid ' + v.color }}>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: v.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{v.icon}</div>
                  <div>
                    <div style={{ fontSize: 16, color: C.text, fontWeight: 700 }}>{t.business_name}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{v.label} · {t.subdomain}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, textAlign: 'center' }}>
                  {[{ v: t.customerCount || 0, l: 'Clients' }, { v: t.serviceCount || 0, l: 'Services' }, { v: t.bookingCount || 0, l: 'Bookings' }].map((s, i) => (
                    <div key={i}><div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{s.v}</div><div style={{ fontSize: 10, color: C.muted }}>{s.l}</div></div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <a href={siteUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#3b82f615', border: '1px solid #3b82f630', borderRadius: 8, color: '#60a5fa', fontSize: 12, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>🌐 Website</a>
                <a href={bookUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#f9731615', border: '1px solid #f9731630', borderRadius: 8, color: '#fb923c', fontSize: 12, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>📅 Booking</a>
                <button onClick={() => { localStorage.setItem('sa_tenant_override', String(t.id)); window.location.href = '/app'; }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#a78bfa15', border: '1px solid #a78bfa30', borderRadius: 8, color: '#a78bfa', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>👁️ Dashboard</button>
                <button onClick={() => copyUrl(siteUrl, 'site-' + t.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#22c55e15', border: '1px solid #22c55e30', borderRadius: 8, color: '#4ade80', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{copied === 'site-' + t.id ? '✓ Copied!' : '📋 Copy Site URL'}</button>
                <button onClick={() => copyUrl(bookUrl, 'book-' + t.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#22c55e15', border: '1px solid #22c55e30', borderRadius: 8, color: '#4ade80', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{copied === 'book-' + t.id ? '✓ Copied!' : '📋 Copy Booking URL'}</button>
              </div>
              <div style={{ display: 'flex', gap: 6, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
                <button onClick={() => onSeed(t)} disabled={seedLoading === t.id} style={{ padding: '6px 14px', background: C.green + '20', border: 'none', borderRadius: 6, color: C.green, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{seedLoading === t.id ? '⏳ Seeding...' : '🌱 Seed Data'}</button>
                <button onClick={() => onResetPw(t)} style={{ padding: '6px 14px', background: C.amber + '20', border: 'none', borderRadius: 6, color: C.amber, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🔑 Password</button>
                <button onClick={() => { if (window.confirm('Reset ALL data for ' + t.business_name + '? This removes all clients, bookings, and services.')) onReset(t); }} style={{ padding: '6px 14px', background: C.red + '20', border: 'none', borderRadius: 6, color: C.red, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🗑 Reset</button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ── MAIN COMPONENT ──────────────────────────
// ── BillingManager ─────────────────────────────────────────────
function BillingManager() {
  const [tenants, setTenants] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(null);
  const [flash, setFlash] = React.useState(null);

  const PLANS = ['trial','founding','professional','enterprise'];
  const STATUSES = ['active','suspended','trialing','canceled','past_due'];
  const PLAN_PRICE = { founding: 99, standard: 199, growth: 149, professional: 249, pro: 249, enterprise: 399, trial: 0 };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/tenants');
      setTenants((r.data.tenants || []).filter(t => t.id !== 1));
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  React.useEffect(() => { load(); }, []);

  const update = async (id, field, value) => {
    setSaving(id);
    try {
      await axios.patch('/api/admin/tenants/' + id, { [field]: value });
      setTenants(p => p.map(t => t.id === id ? { ...t, [field]: value } : t));
      setFlash('Saved!');
      setTimeout(() => setFlash(null), 2000);
    } catch(e) { setFlash('Error: ' + e.message); }
    setSaving(null);
  };

  const totalMRR = tenants.reduce((s, t) => s + (t.status === 'active' && t.plan !== 'trial' ? (PLAN_PRICE[t.plan] || 0) : 0), 0);
  const paying = tenants.filter(t => t.status === 'active' && t.plan !== 'trial').length;
  const trials = tenants.filter(t => t.plan === 'trial').length;

  const inp = { background: '#2e3150', border: '1px solid #2a2d3e', borderRadius: 6, padding: '5px 8px', color: '#fff', fontSize: 12 };

  return (
    <div style={{ padding: '24px 28px' }}>
      {flash && <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: flash.startsWith('Error') ? '#7f1d1d' : '#14532d', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>{flash}</div>}

      <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>💰 Billing Management</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>Manage tenant plans, status, and revenue tracking.</div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Monthly MRR', value: '$' + totalMRR.toLocaleString(), color: '#f5a623' },
          { label: 'Paying Clients', value: paying, color: '#22c55e' },
          { label: 'Active Trials', value: trials, color: '#3b82f6' },
          { label: 'Annual Run Rate', value: '$' + (totalMRR * 12).toLocaleString(), color: '#a855f7' },
        ].map(k => (
          <div key={k.label} style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 10, padding: '16px 20px' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Tenant Table */}
      {loading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
        <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 130px 80px', gap: 0, background: '#1a1c2e', padding: '10px 20px', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 1 }}>
            <div>BUSINESS</div><div>PLAN</div><div>STATUS</div><div>MRR</div><div>ACTIONS</div>
          </div>
          {tenants.map((t, i) => (
            <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 130px 80px', gap: 0, padding: '14px 20px', borderTop: i > 0 ? '1px solid #1e293b' : 'none', alignItems: 'center', background: saving === t.id ? '#2e3150' : 'transparent' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{t.business_name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{t.email} · ID {t.id}</div>
              </div>
              <div>
                <select style={inp} value={t.plan || 'trial'} onChange={e => update(t.id, 'plan', e.target.value)}>
                  {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <select style={{ ...inp, color: t.status === 'active' ? '#22c55e' : t.status === 'suspended' ? '#ef4444' : '#f5a623' }}
                  value={t.status || 'active'} onChange={e => update(t.id, 'status', e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: t.plan !== 'trial' && t.status === 'active' ? '#f5a623' : '#374151' }}>
                {t.plan !== 'trial' && t.status === 'active' ? '$' + (PLAN_PRICE[t.plan] || 0) + '/mo' : '—'}
              </div>
              <div>
                <span style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                  {saving === t.id ? 'Saving...' : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Revenue Summary */}
      <div style={{ marginTop: 24, background: '#1e2535', border: '1px solid #1e293b', borderRadius: 10, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 16 }}>Revenue Breakdown</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tenants.filter(t => t.plan !== 'trial' && t.status === 'active').map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#1a1c2e', borderRadius: 8 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{t.business_name}</span>
                <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>{t.plan} plan</span>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#f5a623' }}>${PLAN_PRICE[t.plan] || 0}/mo</span>
            </div>
          ))}
          {paying === 0 && <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 16 }}>No paying clients yet. Close your first deal! 🚀</div>}
          {paying > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #1e293b', marginTop: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Total MRR</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#f5a623' }}>${totalMRR}/mo</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// DEMO LEADS PANEL
// ============================================================
const STATUS_OPTIONS = ['new','contacted','demo_booked','closed_won','closed_lost'];
const STATUS_META = {
  new:         { bg:'#1E3A5F', color:'#60A5FA', label:'New' },
  contacted:   { bg:'#1C3A2F', color:'#34D399', label:'Contacted' },
  demo_booked: { bg:'#2D1F5E', color:'#A78BFA', label:'Demo Booked' },
  closed_won:  { bg:'#14532D', color:'#4ADE80', label:'Won ✓' },
  closed_lost: { bg:'#3B1515', color:'#F87171', label:'Lost' },
};
function LeadStatusBadge({ status }) {
  const s = STATUS_META[status] || STATUS_META.new;
  return <span style={{ background:s.bg, color:s.color, padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{s.label}</span>;
}
function DemoLeadsTab() {
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('all');
  const [expandedId, setExpandedId] = React.useState(null);
  const [noteEdits, setNoteEdits] = React.useState({});
  const [saving, setSaving] = React.useState({});

  const fetchLeads = React.useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? '/api/demo-leads' : '/api/demo-leads?status=' + filter;
      const r = await axios.get(url);
      setLeads(Array.isArray(r.data) ? r.data : []);
    } catch(e) { console.error('Demo leads fetch failed', e); }
    setLoading(false);
  }, [filter]);

  React.useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateLead = async (id, updates) => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await axios.patch('/api/demo-leads/' + id, updates);
      await fetchLeads();
    } catch(e) { console.error('Update failed', e); }
    setSaving(s => ({ ...s, [id]: false }));
  };

  const counts = leads.reduce((a,l) => { a[l.status]=(a[l.status]||0)+1; return a; }, {});
  const pill = (active) => ({ padding:'5px 14px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', border:'none', background: active ? '#F59E0B' : '#1C1C1C', color: active ? '#000' : '#9CA3AF' });

  return (
    <div style={{ padding:'32px 24px', maxWidth:900, margin:'0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h2 style={{ color:'#F9FAFB', fontSize:22, fontWeight:700, margin:0 }}>🔔 Demo Leads</h2>
          <p style={{ color:'#6B7280', fontSize:13, marginTop:4 }}>{leads.length} total from rental landing page</p>
        </div>
        <button onClick={fetchLeads} style={{ background:'#1C1C1C', color:'#9CA3AF', border:'none', borderRadius:7, padding:'8px 16px', fontSize:12, cursor:'pointer' }}>↻ Refresh</button>
      </div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
        <button style={pill(filter==='all')} onClick={() => setFilter('all')}>All ({leads.length})</button>
        {STATUS_OPTIONS.map(s => (
          <button key={s} style={pill(filter===s)} onClick={() => setFilter(s)}>
            {STATUS_META[s].label}{counts[s] ? ` (${counts[s]})` : ''}
          </button>
        ))}
      </div>
      {loading ? (
        <p style={{ color:'#6B7280', textAlign:'center', paddingTop:40 }}>Loading…</p>
      ) : leads.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#4B5563' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📋</div>
          <p style={{ fontSize:14 }}>No leads yet. Keep dialing.</p>
        </div>
      ) : leads.map(lead => (
        <div key={lead.id}
          onClick={() => setExpandedId(expandedId===lead.id ? null : lead.id)}
          style={{ background:'#111', border:'1px solid ' + (expandedId===lead.id ? '#F59E0B' : '#1C1C1C'), borderRadius:10, padding:'16px 20px', marginBottom:10, cursor:'pointer', transition:'border-color 0.15s' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                <span style={{ color:'#F9FAFB', fontWeight:700, fontSize:15 }}>{lead.name}</span>
                <LeadStatusBadge status={lead.status} />
              </div>
              <div style={{ color:'#9CA3AF', fontSize:13, marginTop:3 }}>
                {lead.business && <span>{lead.business} · </span>}
                <a href={'tel:'+lead.phone} onClick={e=>e.stopPropagation()} style={{ color:'#F59E0B', textDecoration:'none' }}>{lead.phone}</a>
                {lead.email && <span> · {lead.email}</span>}
              </div>
            </div>
            <div style={{ color:'#4B5563', fontSize:11, whiteSpace:'nowrap' }}>
              {new Date(lead.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
            </div>
          </div>
          {expandedId===lead.id && (
            <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #1C1C1C' }} onClick={e=>e.stopPropagation()}>
              {lead.current_software && <p style={{ color:'#6B7280', fontSize:12, marginBottom:12 }}><strong style={{ color:'#9CA3AF' }}>Currently using:</strong> {lead.current_software}</p>}
              <div style={{ marginBottom:12 }}>
                <label style={{ color:'#6B7280', fontSize:11, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Status</label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {STATUS_OPTIONS.map(s => (
                    <button key={s} disabled={saving[lead.id]} onClick={() => updateLead(lead.id,{status:s})}
                      style={{ ...pill(lead.status===s), opacity: saving[lead.id]?0.5:1 }}>
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ color:'#6B7280', fontSize:11, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Notes</label>
                <textarea
                  value={noteEdits[lead.id]!==undefined ? noteEdits[lead.id] : (lead.notes||'')}
                  onChange={e => setNoteEdits(n=>({...n,[lead.id]:e.target.value}))}
                  placeholder="Add notes..."
                  rows={3}
                  style={{ width:'100%', background:'#0F0F0F', border:'1px solid #1C1C1C', borderRadius:6, color:'#F9FAFB', fontSize:13, padding:'10px 12px', resize:'vertical', boxSizing:'border-box' }}
                />
                {noteEdits[lead.id]!==undefined && noteEdits[lead.id]!==(lead.notes||'') && (
                  <button onClick={() => updateLead(lead.id,{notes:noteEdits[lead.id]})} disabled={saving[lead.id]}
                    style={{ marginTop:6, padding:'6px 16px', background:'#F59E0B', color:'#000', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    {saving[lead.id] ? 'Saving…' : 'Save Notes'}
                  </button>
                )}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:14 }}>
                <a href={'tel:'+lead.phone} style={{ padding:'8px 16px', background:'#1C1C1C', color:'#34D399', borderRadius:6, fontSize:12, fontWeight:700, textDecoration:'none' }}>📞 Call</a>
                {lead.email && <a href={'mailto:'+lead.email} style={{ padding:'8px 16px', background:'#1C1C1C', color:'#60A5FA', borderRadius:6, fontSize:12, fontWeight:700, textDecoration:'none' }}>✉️ Email</a>}
                <a href={'sms:'+lead.phone} style={{ padding:'8px 16px', background:'#1C1C1C', color:'#F59E0B', borderRadius:6, fontSize:12, fontWeight:700, textDecoration:'none' }}>💬 Text</a>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [data, setData] = useState(null);
  const [mrrHistory, setMrrHistory] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mrrLoading, setMrrLoading] = useState(true);
  const [pipeLoading, setPipeLoading] = useState(true);
  const [seedLoading, setSeedLoading] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [pwModal, setPwModal] = useState(null); // null or {type:'self'} or {type:'tenant', tenant:{...}}
  const [newPw, setNewPw] = useState('');
  const [oldPw, setOldPw] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  const changeMyPassword = async () => {
    if (!oldPw) return setPwMsg('Enter current password');
    if (newPw.length < 8) return setPwMsg('Min 8 characters');
    setPwLoading(true); setPwMsg('');
    try {
      const r = await axios.post('/api/admin/change-password', { oldPassword: oldPw, password: newPw });
      if (r.data.success) { setPwMsg('Password updated!'); setNewPw(''); setTimeout(() => setPwModal(null), 1500); }
      else setPwMsg(r.data.error);
    } catch(e) { setPwMsg('Error: ' + (e.response?.data?.error || e.message)); }
    setPwLoading(false);
  };

  const resetTenantPassword = async (tenantId) => {
    if (newPw.length < 8) return setPwMsg('Min 8 characters');
    setPwLoading(true); setPwMsg('');
    try {
      const r = await axios.post('/api/admin/tenants/' + tenantId + '/reset-password', { password: newPw });
      if (r.data.success) { setPwMsg('Password reset!'); setNewPw(''); setTimeout(() => setPwModal(null), 1500); }
      else setPwMsg(r.data.error);
    } catch(e) { setPwMsg('Error: ' + (e.response?.data?.error || e.message)); }
    setPwLoading(false);
  };

  const loadData = useCallback(async () => {
    try {
      const r = await axios.get('/api/admin/dashboard');
      if (r.data.success) {
        const d = r.data.dashboard;
        const h = await axios.get('/api/admin/health').catch(() => ({ data: { health: {} } }));
        d.health = h.data.health || {};
        setData(d);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadMRR = useCallback(async () => {
    try { const r = await axios.get('/api/admin/mrr-history'); if (r.data.success) setMrrHistory(r.data.history); } catch(e) {}
    setMrrLoading(false);
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try { const r = await axios.get('/api/admin/audit-log'); if (r.data.success) setAuditLogs(r.data.logs); } catch(e) {}
    setAuditLoading(false);
  }, []);

  const loadPipeline = useCallback(async () => {
    try { const r = await axios.get('/api/admin/pipeline'); if (r.data.success) setPipeline(r.data.pipeline); } catch(e) {}
    setPipeLoading(false);
  }, []);

  useEffect(() => { loadData(); loadMRR(); loadPipeline(); }, [loadData, loadMRR, loadPipeline]);

  // Auto-refresh activity feed every 30s
  const loginAs = (t) => { localStorage.setItem('sa_tenant_override', String(t.id)); window.location.href = '/app'; };

  const seedDemo = async (t) => {
    setSeedLoading(t.id);
    try { await axios.post('/api/admin/tenants/' + t.id + '/seed-demo'); alert('Demo data seeded for ' + t.business_name); loadData(); } catch(e) { alert('Error seeding'); }
    setSeedLoading(null);
  };

  const resetDemo = async (t) => {
    try { await axios.post('/api/admin/tenants/' + t.id + '/reset-demo'); alert('Data cleared for ' + t.business_name); loadData(); } catch(e) { alert('Error resetting'); }
  };

  const getDaysLeft = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;
  const fmtTime = (t) => { if (!t) return ''; const p = t.split(':').map(Number); const h = p[0] > 12 ? p[0] - 12 : p[0] === 0 ? 12 : p[0]; return h + ':' + String(p[1]).padStart(2, '0') + (p[0] >= 12 ? ' PM' : ' AM'); };
  const timeAgo = (d) => { if (!d) return ''; const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 60) return m + 'm ago'; if (m < 1440) return Math.floor(m / 60) + 'h ago'; return Math.floor(m / 1440) + 'd ago'; };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', background: C.bg, color: C.muted }}><p>Loading command center...</p></div>;
  if (!data) return <div style={{ textAlign: 'center', paddingTop: 120, background: C.bg, minHeight: '100vh' }}><p style={{ color: C.muted }}>Failed to load.</p><button onClick={loadData} style={{ marginTop: 12, padding: '10px 24px', background: C.accent, border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Retry</button></div>;

  const s = data.stats;
  const tabs = [
    { key: 'overview', icon: '📊', label: 'Overview' },
    { key: 'pipeline', icon: '🔄', label: 'Pipeline' },
    { key: 'demos', icon: '🧪', label: 'Demos' },
    { key: 'audit', icon: '📋', label: 'Audit Log' },
    { key: 'billing', icon: '💰', label: 'Billing' },
    { key: 'leads', icon: '🔔', label: 'Demo Leads' },
  ];

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text, maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* HEADER */}
      <div style={{ background: C.panel, borderBottom: '1px solid ' + C.border, padding: isMobile ? '14px 16px' : '18px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: C.text, fontSize: isMobile ? 20 : 26, fontWeight: 800, margin: 0 }}>
            {new Date().getHours() < 12 ? '☀️' : new Date().getHours() < 17 ? '🌤' : '🌙'} Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, Mike
          </h1>
          <p style={{ color: C.muted, fontSize: 13, margin: '2px 0 0' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/app/admin" style={{ padding: '10px 20px', background: 'linear-gradient(135deg,' + C.accent + ',' + C.accent2 + ')', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>⚡ Full Admin</Link>
          <button onClick={() => { setPwModal({ type: 'self' }); setNewPw(''); setOldPw(''); setPwMsg(''); }} style={{ padding: '10px 14px', background: C.border, border: 'none', borderRadius: 10, color: C.muted, fontWeight: 600, cursor: 'pointer' }}>🔐</button>
          <button onClick={() => { loadData(); loadMRR(); loadPipeline(); }} style={{ padding: '10px 14px', background: C.border, border: 'none', borderRadius: 10, color: C.muted, fontWeight: 600, cursor: 'pointer' }}>↻</button>
        </div>
      </div>

      {/* TAB BAR */}
      <div style={{ background: C.panel, borderBottom: '1px solid ' + C.border, padding: '0 16px', display: 'flex', gap: 0, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: isMobile ? '10px 10px' : '12px 20px', background: 'none', border: 'none', borderBottom: tab === t.key ? '2px solid ' + C.accent : '2px solid transparent', color: tab === t.key ? C.accent : C.muted, fontWeight: tab === t.key ? 800 : 600, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
            {t.icon} {!isMobile && t.label}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1200, margin: '0 auto' }}>

        {tab === 'overview' && <>
          <TodayFocus tenants={data.tenants || []} mrr={s.totalMRR || 0} />

          {/* ONE-TIME REVENUE */}
          <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 14 }}>💼 One-Time License Sales</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 14 }}>
              {[{label:'Collected',value:'$3,000',color:'#22c55e',sub:'On The Grind deposit'},{label:'Pending',value:'$4,000',color:'#f5a623',sub:'On delivery / milestone'},{label:'Total Deal',value:'$7,000',color:'#a855f7',sub:'Perpetual license'}].map(k => (
                <div key={k.label} style={{ background: '#1a1c2e', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: k.color }}>{k.value}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginTop: 2 }}>{k.label}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{k.sub}</div>
                </div>
              ))}
            </div>
            <div style={{ background: '#1a1c2e', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>On The Grind Equipment Rentals</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 11, background: '#f5a62320', color: '#f5a623', border: '1px solid #f5a62340', borderRadius: 20, padding: '3px 10px', fontWeight: 700 }}>Perpetual License</span>
                <span style={{ fontSize: 11, background: '#22c55e20', color: '#22c55e', border: '1px solid #22c55e40', borderRadius: 20, padding: '3px 10px', fontWeight: 700 }}>$3k Collected</span>
                <span style={{ fontSize: 11, background: '#f5a62320', color: '#f5a623', border: '1px solid #f5a62340', borderRadius: 20, padding: '3px 10px', fontWeight: 700 }}>$4k Pending</span>
              </div>
            </div>
          </div>

          {/* QUIT PROGRESS BAR */}
          {(() => {
            const QUIT_TARGET = 5200;
            const mrr = s.totalMRR || 0;
            const pct = Math.min(100, Math.round((mrr / QUIT_TARGET) * 100));
            const needed = Math.max(0, QUIT_TARGET - mrr);
            const clientsNeeded = needed > 0 ? Math.ceil(needed / 249) : 0;
            const color = pct >= 100 ? '#22c55e' : pct >= 60 ? C.accent : pct >= 30 ? C.amber : '#ef4444';
            return (
              <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>🎯 Quit Your Job Progress</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>${mrr.toLocaleString()} MRR of ${QUIT_TARGET.toLocaleString()} target ({pct}%)</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {pct >= 100
                      ? <div style={{ fontSize: 18, fontWeight: 800, color: '#22c55e' }}>🎉 You can quit!</div>
                      : <div><div style={{ fontSize: 20, fontWeight: 800, color }}>${needed.toLocaleString()}<span style={{ fontSize: 12, color: '#64748b' }}>/mo to go</span></div><div style={{ fontSize: 11, color: '#64748b' }}>{clientsNeeded} more clients at $249/mo</div></div>
                    }
                  </div>
                </div>
                <div style={{ height: 12, background: '#1e293b', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: 6, transition: 'width 0.5s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: '#64748b' }}>
                  <span>$0</span><span style={{ color: C.amber }}>$2,985 (quit zone)</span><span>$5,200 (safe quit)</span>
                </div>
              </div>
            );
          })()}

          {/* MILESTONE ROADMAP */}
          <MilestoneTracker mrr={s.totalMRR || 0} />

          {/* OUTREACH + PROSPECTS */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '300px 1fr', gap: 16, marginBottom: 20 }}>
            <OutreachCounter />
            <ProspectTracker />
          </div>

          {/* TENANT HEALTH */}
          <TenantHealthTable tenants={data.tenants || []} onLoginAs={loginAs} onResetPw={(t) => { setPwModal({ type: 'tenant', tenant: t }); setNewPw(''); setPwMsg(''); }} />

          {/* PLATFORM STATUS + QUICK LINKS */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, padding: '18px 22px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 14 }}>🏥 Platform Status</div>
              {[{label:'API Backend',ok:true},{label:'Database',ok:true},{label:'SMS (Twilio)',ok:true},{label:'Payments (Stripe)',ok:true}].map((h,i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#1a1c2e', borderRadius: 8, marginBottom: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e' }} />
                  <span style={{ color: '#22c55e', fontSize: 13, fontWeight: 600 }}>{h.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>Operational</span>
                </div>
              ))}
            </div>
            <div style={{ background: '#1e2535', border: '1px solid #1e293b', borderRadius: 12, padding: '18px 22px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 14 }}>🔗 Quick Links</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[{icon:'💳',label:'Stripe',url:'https://dashboard.stripe.com'},{icon:'🚂',label:'Railway',url:'https://railway.app/dashboard'},{icon:'🗄️',label:'Supabase',url:'https://supabase.com/dashboard'},{icon:'▲',label:'Vercel',url:'https://vercel.com/dashboard'},{icon:'📱',label:'Twilio',url:'https://console.twilio.com'},{icon:'🐙',label:'GitHub',url:'https://github.com/Mouzer'}].map((l,i) => (
                  <div key={i} onClick={() => window.open(l.url,'_blank')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#1a1c2e', borderRadius: 8, cursor: 'pointer' }}>
                    <span style={{ fontSize: 16 }}>{l.icon}</span>
                    <span style={{ color: '#f0f0f2', fontSize: 13, fontWeight: 600 }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>}

        {tab === 'pipeline' && <Pipeline pipeline={pipeline} loading={pipeLoading} onLoginAs={loginAs} />}
        {tab === 'audit' && (() => {
          if (auditLogs.length === 0 && !auditLoading) loadAudit();
          const actionIcons = { login_as: '👁', reset_password: '🔑', change_own_password: '🔐', seed_demo: '🌱', reset_demo: '🗑', delete_tenant: '❌', create_tenant: '➕', extend_trial: '⏳', broadcast: '📢' };
          const actionColors = { login_as: C.purple, reset_password: C.amber, change_own_password: C.blue, seed_demo: C.green, reset_demo: C.red, delete_tenant: C.red, create_tenant: C.green, extend_trial: C.amber, broadcast: C.blue };
          return (
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: 0 }}>📋 Audit Log</h3>
                <button onClick={loadAudit} style={{ padding: '6px 14px', background: C.border, border: 'none', borderRadius: 8, color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>↻ Refresh</button>
              </div>
              {auditLoading ? <p style={{ color: C.muted }}>Loading...</p> : auditLogs.length === 0 ? <p style={{ color: C.muted }}>No actions recorded yet.</p> : (
                <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                  {auditLogs.map((log, i) => (
                    <div key={log.id || i} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid ' + C.border + '40' }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{actionIcons[log.action] || '📝'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: actionColors[log.action] || C.text }}>{log.action.replace(/_/g, ' ').toUpperCase()}</div>
                        <div style={{ fontSize: 12, color: C.text }}>{log.user_email} → {log.target || '-'}</div>
                        {log.details && Object.keys(log.details).length > 0 && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{JSON.stringify(log.details)}</div>}
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right' }}>
                        <div style={{ fontSize: 11, color: C.muted }}>{new Date(log.created_at).toLocaleDateString()}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{new Date(log.created_at).toLocaleTimeString()}</div>
                        {log.ip_address && <div style={{ fontSize: 10, color: C.muted + '80' }}>{log.ip_address}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })()}
        {tab === 'billing' && <BillingManager />}
        {tab === 'leads' && <DemoLeadsTab />}
        {tab === 'demos' && <DemoManager tenants={(data.tenants || []).filter(t => t.is_demo)} onSeed={seedDemo} onReset={resetDemo} seedLoading={seedLoading} onResetPw={(t) => { setPwModal({ type: 'tenant', tenant: t }); setNewPw(''); setPwMsg(''); }} />}
      </div>

      {/* PASSWORD MODAL */}
      {pwModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setPwModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, padding: 28, width: 380, maxWidth: '90vw' }}>
            <h3 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>
              {pwModal.type === 'self' ? '🔐 Change Your Password' : '🔑 Reset Password'}
            </h3>
            {pwModal.type === 'tenant' && <p style={{ color: C.muted, fontSize: 13, margin: '0 0 16px' }}>For: {pwModal.tenant.business_name} ({pwModal.tenant.email})</p>}
            {pwModal.type === 'self' && <p style={{ color: C.muted, fontSize: 13, margin: '0 0 16px' }}>Update your admin password</p>}
            {pwModal.type === 'self' && <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} placeholder="Current password"
              style={{ width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid ' + C.border, borderRadius: 10, color: C.text, fontSize: 14, marginBottom: 12, outline: 'none', boxSizing: 'border-box' }} />}
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password (min 8 chars)"
              style={{ width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid ' + C.border, borderRadius: 10, color: C.text, fontSize: 14, marginBottom: 12, outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              {[8, 'A-Z', '0-9', '!@#'].map((rule, i) => {
                const pass = i === 0 ? newPw.length >= 8 : i === 1 ? /[A-Z]/.test(newPw) : i === 2 ? /[0-9]/.test(newPw) : /[^a-zA-Z0-9]/.test(newPw);
                return <span key={i} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: pass ? C.green + '20' : C.border, color: pass ? C.green : C.muted, fontWeight: 600 }}>{typeof rule === 'number' ? rule + '+ chars' : rule}</span>;
              })}
            </div>
            {pwMsg && <p style={{ color: pwMsg.includes('updated') || pwMsg.includes('reset') ? C.green : C.red, fontSize: 13, marginBottom: 12 }}>{pwMsg}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setPwModal(null)} style={{ flex: 1, padding: 12, background: C.border, border: 'none', borderRadius: 10, color: C.muted, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => pwModal.type === 'self' ? changeMyPassword() : resetTenantPassword(pwModal.tenant.id)} disabled={pwLoading || newPw.length < 8 || (pwModal.type === 'self' && !oldPw)}
                style={{ flex: 1, padding: 12, background: newPw.length >= 8 ? 'linear-gradient(135deg,' + C.accent + ',' + C.accent2 + ')' : C.border, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: pwLoading ? 'wait' : 'pointer', opacity: newPw.length < 8 ? 0.5 : 1 }}>
                {pwLoading ? '...' : pwModal.type === 'self' ? 'Update Password' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
